import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Голосовой ввод через Web Speech API.
 *
 * Аня диктует голосовые вместо того, чтобы печатать, Вика печатать не любит —
 * поэтому микрофон здесь не украшение, а основной способ ввода.
 *
 * Распознаёт браузер, своего сервиса распознавания у нас нет и не планируется:
 * ради одного поля ввода держать модель — несоразмерно.
 *
 * Ограничения, о которых честно говорим пользователю:
 *   • работает в Chrome и Edge; в Firefox API нет, в Safari частичный;
 *   • браузер отправляет звук на серверы Google — распознавание не локальное.
 *     Диктуют содержимое смет, поэтому знать об этом стоит.
 */

interface SpeechRecognitionAlternativeLike {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: { length: number; [i: number]: SpeechRecognitionResultLike };
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}

type RecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): RecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface Speech {
  /** API есть в этом браузере */
  supported: boolean;
  listening: boolean;
  /** ещё не распознанный до конца кусок — показываем в поле, пока человек говорит */
  interim: string;
  error: string | null;
  toggle: () => void;
  stop: () => void;
}

export function useSpeech(onFinal: (text: string) => void): Speech {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<SpeechRecognitionLike | null>(null);

  // Наличие API проверяем после монтирования: на сервере window нет, и
  // отрендерить кнопку по-разному на сервере и на клиенте нельзя.
  useEffect(() => setSupported(getRecognitionCtor() !== null), []);

  const stop = useCallback(() => {
    ref.current?.stop();
    ref.current = null;
    setListening(false);
    setInterim("");
  }, []);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;

    const rec = new Ctor();
    rec.lang = "ru-RU";
    // Одна фраза за нажатие: диктуют правку и отпускают, а не ведут поток.
    rec.continuous = false;
    rec.interimResults = true;

    rec.onresult = (e) => {
      let partial = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) onFinal(res[0].transcript.trim());
        else partial += res[0].transcript;
      }
      setInterim(partial);
    };

    rec.onerror = (e) => {
      setError(
        e.error === "not-allowed"
          ? "Браузер не дал доступ к микрофону"
          : e.error === "no-speech"
            ? "Не расслышал"
            : `Распознавание не сработало: ${e.error}`,
      );
      setListening(false);
      setInterim("");
    };

    rec.onend = () => {
      setListening(false);
      setInterim("");
      ref.current = null;
    };

    ref.current = rec;
    setError(null);
    setListening(true);
    rec.start();
  }, [onFinal]);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  useEffect(() => () => ref.current?.abort(), []);

  return { supported, listening, interim, error, toggle, stop };
}
